import { z } from 'zod'

import { ProblemSchema, ProblemSourceTypeSchema } from '~/@entities/Problem'
import { type ScorableProblem, type ScoringItem, ScoringResultSchema } from '~/@entities/Scoring'
import { StudentIdSchema, StudentSchema } from '~/@entities/Student'
import {
  WorkbookIdSchema,
  WorkbookPageSchema,
  WorkbookProblemSchema,
  WorkbookSchema,
  WorkbookSecuringTypeSchema,
} from '~/@entities/Workbook'
import { LearningStatusSchema, OriginWorkbookSchema } from '~/@entities/Worksheet'
import { sliceSchema } from '~/schema/slice'

// 학생-교재(출제된 교재) > 회차(revision) > 진도(progress, 교재 페이지와 1:1) > 문제 순으로 매달린다.

/***************** 풀이 영상 시청 기록 *****************/
// 채점 전·후에 풀이 영상을 봤는가. 서버 Assist
export const SOLVING_VIDEO_ASSIST = [
  'WATCHED_SOLVING_VIDEO_BEFORE_SCORING',
  'WATCHED_SOLVING_VIDEO_AFTER_SCORING',
] as const

export const SolvingVideoAssistSchema = z.enum(SOLVING_VIDEO_ASSIST)
export type SolvingVideoAssist = z.infer<typeof SolvingVideoAssistSchema>

// 서버는 Asia/Seoul LocalDateTime 을 타임존 없이 낸다.
const _LocalDateTimeSchema = z.iso.datetime({ local: true })

/** 학생에게 출제된 교재 한 건 */
export const StudentWorkbookSchema = z.object({
  id: z.number(),
  workbookId: WorkbookIdSchema,
  studentId: StudentIdSchema,
  createDatetime: _LocalDateTimeSchema,
})
export type StudentWorkbook = z.infer<typeof StudentWorkbookSchema>

/** 출제된 교재의 회차. 같은 교재를 다시 출제하면 회차가 는다 */
export const StudentWorkbookRevisionSchema = z.object({
  id: z.number(),
  studentWorkbookId: z.number(),
  round: z.number(),
  createDatetime: _LocalDateTimeSchema,
  lastUseDatetime: _LocalDateTimeSchema,
  recentProgressId: z.number(),
  recentPageNumber: z.number(),
  percent: z.number(),
})
export type StudentWorkbookRevision = z.infer<typeof StudentWorkbookRevisionSchema>

/** 교재의 한 페이지. 진도 한 건이 여기에 매달린다 */
export const StudentWorkbookPageSchema = z.object({
  id: z.number(),
  workbookId: z.number(),
  page: z.number(),
  title: z.string(),
})
export type StudentWorkbookPage = z.infer<typeof StudentWorkbookPageSchema>

/** 회차 안의 한 페이지 진도. 서버 enum 은 EXPIRED 가 없는 셋이지만 학습 상태 어휘를 같이 쓴다 */
export const StudentWorkbookProgressSchema = z.object({
  id: z.number(),
  status: LearningStatusSchema,
  openDatetime: _LocalDateTimeSchema.nullable(),
})
export type StudentWorkbookProgress = z.infer<typeof StudentWorkbookProgressSchema>

/***************** 문항 채점 *****************/

// 교재 문제의 채점은 문제(workbookProblemId)와 진도(studentWorkbookProgressId) 둘로 가리킨다. 서버가 채점 id 를
// 주지 않아 화면은 둘을 이은 문자열을 id 로 쓰고, 서버에 보낼 때는 둘을 그대로 싣는다.
const _WorkbookScoringKeySchema = z.object({
  workbookProblemId: z.number(),
  studentWorkbookProgressId: StudentWorkbookProgressSchema.shape.id,
})
type WorkbookScoringKey = z.infer<typeof _WorkbookScoringKeySchema>
export type WorkbookScoringId = `${number}-${number}`

export const toWorkbookScoringId = ({
  workbookProblemId,
  studentWorkbookProgressId,
}: WorkbookScoringKey): WorkbookScoringId => `${workbookProblemId}-${studentWorkbookProgressId}`

// 교재 종류마다 문제 모양이 달라(나의 교재는 문제은행 문제, 시중교재·교과서는 교재 문제) 채점이 읽는 필드만
// 공통으로 요구한다. 교재 종류별 모양(이미지 유무 등)은 교재(Workbook)가 정한다.
export type WorkbookScoringProblem = ScorableProblem

/**
 * 교재 채점이 읽는 문제. 교재 문제 위에 짝지은 문제은행 문제(내 교재·학원그룹은 problem, 시중교재·교과서는 엑스토리)가
 * 겹쳐져 이미지·풀이 영상과 정답이 그쪽 값이 된다. 문제은행 문제가 없는 교재(시중교재·교과서의 대부분)는 교재 문제
 * 그대로다. 채점이 읽는 칸(ScorableProblem)은 문제은행 문제 모양을 따른다.
 */
export const StudentWorkbookScoringProblemSchema = ProblemSchema.pick({
  answer: true,
  type: true,
  keypadTypes: true,
  answerData: true,
  answerCount: true,
  answerUnits: true,
  level: true,
  optionCount: true,
  autoScoredType: true,
}).extend({
  id: WorkbookProblemSchema.shape.id,
  problemId: ProblemSchema.shape.id.nullable(),
  conceptId: WorkbookProblemSchema.shape.conceptId,
  answerImageUrl: WorkbookProblemSchema.shape.answerImageUrl,
  problemImageUrl: WorkbookProblemSchema.shape.problemImageUrl,
  solutionImageUrl: WorkbookProblemSchema.shape.solutionImageUrl,
  video: WorkbookProblemSchema.shape.video,
  videoMatched: z.boolean().optional(),
  answerConfirmed: WorkbookProblemSchema.shape.answerConfirmed,
  modifiedFromOriginalFlag: WorkbookProblemSchema.shape.modifiedFromOriginalFlag,
})
export type StudentWorkbookScoringProblem = z.infer<typeof StudentWorkbookScoringProblemSchema>

/** 교재 문항 하나의 채점. 문항 번호·자동채점 여부·학년은 화면이 아는 교재에서 온다 */
export type StudentWorkbookScoring<P extends WorkbookScoringProblem = WorkbookScoringProblem> =
  ScoringItem<{ kind: 'WORKBOOK'; id: WorkbookScoringId } & WorkbookScoringKey, P>

/***************** 학생 교재 조회 *****************/

/**
 * 학생의 교재 목록 한 줄. 교재에 학생 교재·최근 회차 정보가 붙는다.
 * roundToRevisionRoundMap 의 키는 회차 번호(문자열)다.
 * 제외한 서버 필드: originWorkbook(앱이 읽지 않는다)·progress(recentPageNumber 의 옛 이름)
 */
export const StudentWorkbookListItemSchema = z.object({
  workbook: WorkbookSchema,
  studentWorkbook: StudentWorkbookSchema,
  recentRevisionId: StudentWorkbookRevisionSchema.shape.id,
  recentRevisionRound: StudentWorkbookRevisionSchema.shape.round,
  recentPageNumber: z.number(),
  recentAssignedDatetime: _LocalDateTimeSchema,
  roundToRevisionRoundMap: z.record(z.string(), StudentWorkbookRevisionSchema),
})
export type StudentWorkbookListItem = z.infer<typeof StudentWorkbookListItemSchema>

/**
 * 회차 안의 페이지 하나와 그 진도. 시그니처·학원그룹 교재 페이지는 원본 이미지를 싣는다.
 * 제외한 서버 필드: workbookPage(page 와 같은 객체를 한 번 더 보낸다)
 */
export const StudentWorkbookPageProgressSchema = StudentWorkbookProgressSchema.omit({
  id: true,
}).extend({
  progressId: StudentWorkbookProgressSchema.shape.id,
  page: StudentWorkbookPageSchema.extend({ imageUrl: z.string().optional() }),
})
export type StudentWorkbookPageProgress = z.infer<typeof StudentWorkbookPageProgressSchema>

/** 학생 교재 상세: 교재·회차와 회차의 페이지별 진도 */
export const StudentWorkbookProgressListSchema = z.object({
  workbook: WorkbookSchema,
  round: StudentWorkbookRevisionSchema.shape.round,
  page: sliceSchema(StudentWorkbookPageProgressSchema),
})
export type StudentWorkbookProgressList = z.infer<typeof StudentWorkbookProgressListSchema>

/**
 * 학생이 푼 교재 문제와 그 채점. userAnswers 는 서버가 저장된 문자열을 배열로 풀어 낸다(풀지 못하면 null).
 * 제외한 서버 필드: scoring.assists(풀이 영상 시청 기록. 앱은 시청을 보내기만 하고 읽지 않는다)
 */
export const StudentWorkbookScoredProblemSchema = WorkbookProblemSchema.extend({
  scoring: z.object({
    studentWorkbookProgressId: StudentWorkbookProgressSchema.shape.id,
    workbookProblemId: WorkbookProblemSchema.shape.id,
    userAnswer: z.string().nullable(),
    userAnswers: z.array(z.string().nullable()).nullable(),
    result: ScoringResultSchema,
    updateDatetime: _LocalDateTimeSchema,
  }),
  // 학생에게 유형 이름을 감추는 교재인가
  conceptHidden: z.boolean(),
})
export type StudentWorkbookScoredProblem = z.infer<typeof StudentWorkbookScoredProblemSchema>

/***************** 선생님 앱의 학생 교재 조회 *****************/
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 studentWorkbook/controller/StudentWorkbookController.kt 와
// controller/response/·model/dto/ 의 응답 클래스로 확인.

// 내 교재를 시중교재로 만들었을 때의 바탕 교재. 서버는 보안 등급이 없으면 키를 빼지 않고 null 을 싣는다
const _OriginWorkbookSchema = OriginWorkbookSchema.extend({
  securingType: WorkbookSecuringTypeSchema.nullable(),
})

/** 선생님 앱이 보는 학생 교재 목록 한 줄. 학생 앱이 받는 줄에 바탕 교재가 더 붙는다 */
export const StudentWorkbookListItemDetailSchema = StudentWorkbookListItemSchema.extend({
  originWorkbook: _OriginWorkbookSchema.nullable(),
})
export type StudentWorkbookListItemDetail = z.infer<typeof StudentWorkbookListItemDetailSchema>

/** 학생 교재 채점 한 건. 선생님 앱은 풀이 영상 시청 기록도 읽는다 */
export const StudentWorkbookScoringDetailSchema =
  StudentWorkbookScoredProblemSchema.shape.scoring.extend({
    assists: z.array(SolvingVideoAssistSchema),
  })
export type StudentWorkbookScoringDetail = z.infer<typeof StudentWorkbookScoringDetailSchema>

/**
 * 내 교재·학원그룹 교재 문제가 품은 문제은행 문제. 선생님 앱은 출처(나의 DB 문제는 오류 신고를 막는다)도 읽는다.
 * 정답 AST 는 V2 문제 모양이지만 이 응답(ProblemDto)에는 실려 온다.
 */
export const WorkbookProblemSourceProblemSchema = ProblemSchema.extend({
  sourceType: ProblemSourceTypeSchema,
})
export type WorkbookProblemSourceProblem = z.infer<typeof WorkbookProblemSourceProblemSchema>

/** 선생님 앱이 보는 학생 교재 채점 문항. 선생님이 붙인 문제 태그와 풀이 영상 시청 기록이 더 붙는다 */
export const StudentWorkbookScoredProblemDetailSchema = StudentWorkbookScoredProblemSchema.extend({
  tagTop: z.string().nullable(),
  problem: WorkbookProblemSourceProblemSchema.nullable().optional(),
  scoring: StudentWorkbookScoringDetailSchema,
})
export type StudentWorkbookScoredProblemDetail = z.infer<
  typeof StudentWorkbookScoredProblemDetailSchema
>

/***************** 학년·반 교재 *****************/

// 서버 StudentWorkbookProgressType. 학습 상태 어휘에서 만료(EXPIRED)가 없는 셋이다
const _WorkbookProgressStatusSchema = LearningStatusSchema.exclude(['EXPIRED'])

/** 학년·반 교재의 한 회차를 받은 학생 한 명 */
export const GroupStudentWorkbookRevisionSchema = z.object({
  studentWorkbookId: StudentWorkbookSchema.shape.id,
  revisionId: StudentWorkbookRevisionSchema.shape.id,
  round: StudentWorkbookRevisionSchema.shape.round,
  recentWorkbookPageId: WorkbookPageSchema.shape.id,
  recentProgressId: StudentWorkbookProgressSchema.shape.id,
  studentId: StudentSchema.shape.id,
  studentName: StudentSchema.shape.name,
  createDatetime: _LocalDateTimeSchema,
  lastUseDatetime: _LocalDateTimeSchema,
})
export type GroupStudentWorkbookRevision = z.infer<typeof GroupStudentWorkbookRevisionSchema>

/**
 * 학년·반에 출제된 교재 한 권. roundToDetailMap 의 키는 회차 번호(문자열)다.
 * 마지막 회차·최근 출제일은 회차가 하나도 없으면 null 이다.
 */
export const GroupStudentWorkbookSchema = z.object({
  workbook: WorkbookSchema,
  roundToDetailMap: z.record(
    z.string(),
    z.object({
      recentWorkbookPageId: WorkbookPageSchema.shape.id,
      revisionList: z.array(GroupStudentWorkbookRevisionSchema),
    }),
  ),
  originWorkbook: _OriginWorkbookSchema.nullable(),
  lastRound: StudentWorkbookRevisionSchema.shape.round.nullable(),
  recentAssignedDatetime: _LocalDateTimeSchema.nullable(),
})
export type GroupStudentWorkbook = z.infer<typeof GroupStudentWorkbookSchema>

/** 학년·반 교재의 한 페이지 진도를 학생별로. 학생 하나라도 풀고 있으면 status 는 PROGRESS 다 */
export const GroupWorkbookPageProgressSchema = z.object({
  workbookPage: WorkbookPageSchema,
  status: _WorkbookProgressStatusSchema,
  studentWorkbookProgresses: z.array(
    z.object({
      id: StudentWorkbookProgressSchema.shape.id,
      revisionId: StudentWorkbookRevisionSchema.shape.id,
      workbookPageId: WorkbookPageSchema.shape.id,
      score: z.number().nullable(),
      status: _WorkbookProgressStatusSchema,
      updateDatetime: _LocalDateTimeSchema,
      openDatetime: _LocalDateTimeSchema.nullable(),
      createDatetime: _LocalDateTimeSchema,
    }),
  ),
})
export type GroupWorkbookPageProgress = z.infer<typeof GroupWorkbookPageProgressSchema>

/** 학년·반 교재의 문제 하나와 학생들의 채점. summary 는 정답·오답(모름 포함)·미채점 학생 수다 */
export const GroupWorkbookScoredProblemSchema = WorkbookProblemSchema.extend({
  tagTop: z.string().nullable(),
  problem: WorkbookProblemSourceProblemSchema.nullable().optional(),
  summary: z.object({
    correct: z.number(),
    wrong: z.number(),
    none: z.number(),
    wrongStudentNameList: z.array(StudentSchema.shape.name),
  }),
  list: z.array(StudentWorkbookScoringDetailSchema),
})
export type GroupWorkbookScoredProblem = z.infer<typeof GroupWorkbookScoredProblemSchema>
